<template>
  <div :class="['type-' + resource.type]">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />

    <local-version-banner v-if="resource.local_version" :content="resource.local_version" />

    <div
      v-if="resource.header_image"
      class="section-resource-banner"
      :style="{ backgroundImage: 'url(' + resource.header_bg_image + ')' }"
    >
      <img
        v-if="resource.media_thumbnail"
        :src="resource.media_thumbnail.medium.src"
        alt=""
        width="382"
        height="200"
      />
    </div>

    <div class="row">
      <div class="columns small-12 medium-8 large-7">
        <header>
          <div :class="['text-' + resource.type, 'subheader']">
            <template v-if="resource.is_free">Free</template>
            Teaching Resource Pack
          </div>
          <h1>{{ resource.name }}</h1>

          <template v-if="resource.breadcrumbs">
            <t-breadcrumbs :items="resource.breadcrumbs" class="tw-mt-1" />
          </template>
        </header>

        <div class="section section-actions flex flex-wrap align-middle">
          <download-button
            :files="resource.files"
            :resource-type="resource.type"
            :is-free="resource.is_free"
          />
          <action-buttons class="pull-right" :resource-id="resource.id" />
        </div>

        <div class="callout callout-details">
          <publisher-link v-if="resource.publisher" :publisher="resource.publisher" />
          <div>
            {{ formatFileFormats(resource.file_formats) }}
            <template v-if="resource.page_count">
              <span>|</span>
              {{ resource.resources.length }}
              {{ pluralise('resource', resource.resources.length) }}
            </template>
            <template v-if="resource.year_levels_range">
              <span>|</span>
              {{ $t('grade_label') }}s:
              <strong>{{ resource.year_levels_range }}</strong>
            </template>
          </div>
        </div>

        <section class="section section-content">
          <p>
            <strong>{{ resource.excerpt }}</strong>
          </p>
          <div class="description">
            <div v-if="resource.content" v-html="resource.content"></div>

            <ul v-if="resource.resources">
              <li v-for="resource in resource.resources" :key="resource.slug">
                <nuxt-link :to="resource.link" title="View and download this resource">
                  {{ resource.name }}
                </nuxt-link>
              </li>
            </ul>
          </div>
        </section>

        <section
          v-if="resource.videos && resource.videos.length > 0"
          class="section section-player"
        >
          <div v-for="video in resource.videos" :key="video.id" class="flex-video">
            <wistia-video :title="resource.name" :wistia-id="video.name" embed="inline" />
          </div>
        </section>

        <section v-if="isAuthenticated && resource.files" class="section section-download">
          <h3>Select a download</h3>
          <div class="box no-padding">
            <download-link
              :files="resource.files"
              :resource-type="resource.type"
              :is-free="resource.is_free"
              :event-params="resource.event_params"
              position="Content | Select a download"
              click-type="Text Link"
              click-text=""
            />
          </div>
        </section>

        <section
          v-if="resource.curriculums && resource.curriculums.length > 0"
          class="section section-curriculum"
        >
          <h3>Curriculum</h3>
          <curriculum-list :curriculums="resource.curriculums" />
        </section>

        <section v-if="resource.author" class="section section-author">
          <h3>Author</h3>

          <div class="flex">
            <figure v-if="resource.author && resource.author.photo">
              <img
                :src="resource.author.photo"
                class="circle"
                :alt="'Photo of ' + resource.author.display_name"
                height="42"
                width="42"
              />
            </figure>
            <span>{{ resource.author.display_name }}</span>
          </div>
        </section>

        <section v-if="resource.publisher" class="section section-publisher">
          <div class="flex">
            <figure v-if="resource.publisher && resource.publisher.logo">
              <img
                :src="resource.publisher.logo_square"
                class="circle"
                height="42"
                width="42"
                :alt="resource.publisher.name"
              />
            </figure>
            <div>
              <h3>{{ resource.publisher.name }}</h3>
              <p>{{ resource.publisher.excerpt }}</p>
            </div>
          </div>
        </section>

        <tag-list
          :file-formats="resource.file_formats"
          :tags="resource.tags"
          :year-levels="resource.year_levels"
          :country="resource.country"
        />
      </div>

      <aside class="columns small-12 medium-4 large-5">
        <div>
          <div v-if="resource.resources && resource.resources.length > 0">
            <h4 class="text-center">Pack includes:</h4>

            <ul class="row block-grid block-grid-cards small-up-2 medium-up-1 large-up-2">
              <li v-for="item in resource.resources" :key="item.id">
                <card v-if="item && item.link" type="browse" :item="item" :url="item.link" />
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </div>

    <comment-section
      class="section-comments"
      :resource-id="resource.id"
      :comment-count="resource.comments"
      :resource-updates="resource.updates"
      :event-params="resource.event_params"
    />

    <resources-list
      align="center"
      :load-more="true"
      :params="{ id: resource.id }"
      title="More time-saving resources!"
      list-type="related"
    />
    <country-switcher :link="resource.link" :linked="resource.linked" :country="resource.country" />
  </div>
</template>

<script>
/* A single teaching resource
 *
 * @parent:
 * @requests: /public/v1/resource
 * @events:
 * @props:
 * @components: ActionButtons, AuthorToolbar, TagList, CommentSection, CurriculumList, DownloadButton, DownloadLink, ImageGallery, ResourcesList, WistiaVideo
 * @methods:
 */

import { mapGetters } from 'vuex';

import ActionButtons from '~/components/resource/ActionButtons.vue';
import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import Card from '~/components/card/Card.vue';
import DownloadButton from '~/components/resource/DownloadButton.vue';
import DownloadLink from '~/components/resource/DownloadLink.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import TagList from '~/components/resource/TagList.vue';
import CommentSection from '~/components/comment/CommentSection.vue';
import CurriculumList from '~/components/resource/CurriculumList.vue';
import PublisherLink from '~/components/PublisherLink.vue';
import WistiaVideo from '~/components/WistiaVideo.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { pluralise } from '~/utils/formatStrings';
import { formatFileFormats } from '~/utils/download';

export default {
  name: 'TeachingResourcePack',
  components: {
    ActionButtons,
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    TBreadcrumbs,
    Card,
    CommentSection,
    DownloadButton,
    DownloadLink,
    ResourcesList,
    LocalVersionBanner: () => import('~/components/resource/LocalVersionBanner.vue'),
    TagList,
    CurriculumList,
    PublisherLink,
    WistiaVideo,
    CountrySwitcher,
  },
  validate({ _params }) {
    return true;
    //return /\s/g.test(params.slug);
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['resource-pack'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Resource Pack',
      description: 'A teaching resource pack from Teach Starter',
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  head() {
    return {
      title: this.title,
      meta: this.$metaPropertiesResource(this.resource),
      link: this.$hrefLangsResource(
        this.resource.linked,
        this.resource.link,
        this.resource.country
      ),
      script: [
        {
          hid: 'wistia',
          src: 'https://fast.wistia.com/assets/external/E-v1.js',
          defer: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'loggedInUser', 'resource']),
  },
  mounted() {
    this.$mzAnalyticsApi({
      event: 'pageView',
      name: 'resource_pack',
      productArea: 'pack',
      entityType: 'resource_pack',
      entityId: this.resource.id,
    });
    if (this.resource.event_params_intercom) {
      const metadata = this.resource.event_params_intercom;
      if (typeof this.$intercom !== 'undefined') {
        this.$intercom.trackEvent('Resource Viewed', metadata);
      }
    }
  },
  methods: {
    pluralise,
    formatFileFormats,
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/sections/_resource-page.scss';
</style>
